import { useState, useEffect } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, ArrowRightLeft } from "lucide-react";

const GROUPS = ["territories", "leads", "opportunities", "advertisers", "tasks", "renewals", "team"];
const UNASSIGNED = "__unassigned";

// mode: "replace" (transfer, optionally remove) | "delete" (permanent delete with transfer or unassign)
export default function ReplaceStaffDialog({ target, mode, staff, onClose, onDone }) {
  const [sum, setSum] = useState(null);
  const [to, setTo] = useState("");
  const [groups, setGroups] = useState(GROUPS);
  const [remove, setRemove] = useState(true);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!target) return;
    setSum(null); setTo(""); setGroups(GROUPS); setRemove(mode === "delete" || !target.protected); setTyped("");
    api.get(`/users/${target.id}/transfer-summary`).then((r) => setSum(r.data)).catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, [target, mode]);
  if (!target) return null;
  const isDelete = mode === "delete";
  const removing = isDelete || remove;
  const hasRecords = sum && sum.total > 0;
  const needTo = !isDelete || hasRecords;
  const canSubmit = sum && (!needTo || to) && (!removing || typed === "DELETE") && !busy;
  const options = staff.filter((u) => u.id !== target.id && u.active !== false && u.invite_status !== "Cancelled");

  const submit = async () => {
    setBusy(true);
    try {
      const body = { to_user: to && to !== UNASSIGNED ? to : null, groups: to === UNASSIGNED ? [] : groups, remove: removing };
      const { data } = await api.post(`/users/${target.id}/transfer`, body);
      toast.success(`${data.records_transferred} record(s) transferred to ${data.to}${data.left_unassigned ? ` · ${data.left_unassigned} left unassigned` : ""}${data.removed ? ` · ${target.name} removed` : ""}`);
      onDone();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="replace-staff-dialog">
        <DialogHeader>
          <DialogTitle className={`font-display ${isDelete ? "text-[#C42607]" : ""}`}>{isDelete ? "Permanently Delete Staff Member?" : "Replace / Transfer Staff"}</DialogTitle>
          <DialogDescription>Current staff member: <span className="font-semibold text-slate-700">{target.name}</span></DialogDescription>
        </DialogHeader>
        {!sum ? <p className="text-sm text-slate-400 py-2">Checking their workload…</p> : (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-3" data-testid="transfer-summary">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Active workload</p>
              {GROUPS.map((g) => (
                <label key={g} className="flex items-center justify-between gap-2 py-0.5 text-sm">
                  <span className="flex items-center gap-2">
                    {needTo && to !== UNASSIGNED && <Checkbox checked={groups.includes(g)} onCheckedChange={(v) => setGroups(v ? [...groups, g] : groups.filter((x) => x !== g))} data-testid={`transfer-group-${g}`} />}
                    {sum.labels[g]}
                  </span>
                  <span className="tnum font-semibold" data-testid={`transfer-count-${g}`}>{sum.counts[g]}</span>
                </label>
              ))}
              {sum.territory_names?.length > 0 && <p className="mt-1 text-[11px] text-slate-400">Markets: {sum.territory_names.join(", ")}</p>}
              <p className="mt-1.5 text-[11px] text-slate-400">Contracts (who made the sale), commissions (who earned them), notes, activity and audit history stay exactly as they are.</p>
            </div>
            {isDelete && hasRecords && <p className="text-sm text-[#B45309]" data-testid="delete-has-records">This staff member still has assigned CRM records. Choose what should happen to them before deleting the account.</p>}
            {needTo && (
              <div><Label className="text-xs">Transfer to</Label>
                <Select value={to} onValueChange={setTo}>
                  <SelectTrigger className="mt-1" data-testid="transfer-to"><SelectValue placeholder="Select staff member…" /></SelectTrigger>
                  <SelectContent>
                    {options.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    {isDelete && <SelectItem value={UNASSIGNED}>Leave records unassigned</SelectItem>}
                  </SelectContent>
                </Select></div>
            )}
            {!isDelete && !target.protected && (
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={remove} onCheckedChange={(v) => setRemove(!!v)} data-testid="transfer-remove" />Also permanently remove {target.name}'s staff account</label>
            )}
            {removing && (
              <div className="rounded-lg border border-[#C42607]/30 bg-[#C42607]/5 p-3 text-sm" data-testid="delete-confirm-block">
                <p>Their login, staff profile, permissions, sessions and Outlook connection will be removed. Business records, sales history, notes and CRM history will <span className="font-semibold">NOT</span> be deleted. Old records will show them as former staff.</p>
                <Label className="mt-2 block text-xs">Type DELETE to confirm</Label>
                <Input className="mt-1 bg-white" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-input" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="transfer-cancel">Cancel</Button>
          <Button className={removing ? "bg-[#C42607] hover:bg-[#a51f06]" : ""} disabled={!canSubmit} onClick={submit} data-testid="transfer-submit">
            {removing ? <Trash2 className="h-4 w-4" /> : <ArrowRightLeft className="h-4 w-4" />}
            {isDelete ? "Permanently Delete Staff" : removing ? "Transfer & Remove Staff" : "Transfer Responsibilities"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
